import { developmentDetector } from './developmentModeDetector';

/**
 * Global Script Blocker - Phase 4F
 * Nuclear shutdown of all external scripts and tracking
 */

class GlobalScriptBlocker {
  private config = developmentDetector.getConfig();
  private blockedDomains = [
    'googletagmanager.com',
    'google-analytics.com',
    'googleadservices.com',
    'facebook.com',
    'facebook.net',
    'connect.facebook.net',
    'twitter.com',
    'tiktok.com',
    'reddit.com',
    'linkedin.com',
    'hotjar.com',
    'mixpanel.com',
    'segment.com',
    'amplitude.com',
    'intercom.io',
    'zendesk.com',
    'drift.com',
    'hubspot.com'
  ];
  private mutationObserver: MutationObserver | null = null;

  constructor() {
    if (this.config.isDevelopment) {
      this.initializeNuclearShutdown();
    }
  }

  private initializeNuclearShutdown(): void {
    console.log('🚨 [NUCLEAR SHUTDOWN] Initializing complete external call blocking');
    
    // Block existing scripts
    this.removeExistingScripts();
    
    // Set up DOM mutation observer
    this.setupDOMMutationObserver();
    
    // Intercept fetch and XMLHttpRequest
    this.interceptNetworkRequests();
    
    // Block global tracking objects
    this.blockGlobalTrackingObjects();
  }

  private removeExistingScripts(): void {
    const scripts = document.querySelectorAll('script[src]');
    let removedCount = 0;

    scripts.forEach((script) => {
      const src = script.getAttribute('src');
      if (src && this.shouldBlockUrl(src)) {
        script.remove();
        removedCount++;
        console.log(`🚫 [BLOCKED] Removed external script: ${src}`);
      }
    });

    if (removedCount > 0) {
      console.log(`🧹 [CLEANUP] Removed ${removedCount} external scripts`);
    }
  }

  private setupDOMMutationObserver(): void {
    // Throttle mutations to reduce console noise
    let mutationTimeout: NodeJS.Timeout;
    const pendingMutations: MutationRecord[] = [];

    this.mutationObserver = new MutationObserver((mutations) => {
      pendingMutations.push(...mutations);
      
      clearTimeout(mutationTimeout);
      mutationTimeout = setTimeout(() => {
        this.processPendingMutations(pendingMutations);
        pendingMutations.length = 0;
      }, 100);
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    console.log('👁️ [MONITOR] DOM mutation observer active - blocking external scripts');
  }

  private processPendingMutations(mutations: MutationRecord[]): void {
    let blockedCount = 0;
    
    try {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            
            // Check for script tags
            if (element.tagName === 'SCRIPT') {
              const src = element.getAttribute('src');
              if (src && this.shouldBlockUrl(src)) {
                element.remove();
                blockedCount++;
              }
            }
            
            // Check for iframe tags (tracking pixels)
            if (element.tagName === 'IFRAME') {
              const src = element.getAttribute('src');
              if (src && this.shouldBlockUrl(src)) {
                element.remove();
                blockedCount++;
              }
            }
            
            // Check for img tags (tracking pixels)
            if (element.tagName === 'IMG') {
              const src = element.getAttribute('src');
              if (src && this.shouldBlockUrl(src)) {
                element.remove();
                blockedCount++;
              }
            }

            // Check child elements
            const childElements = element.querySelectorAll('script[src], iframe[src], img[src]');
            childElements.forEach((child) => {
              const src = child.getAttribute('src');
              if (src && this.shouldBlockUrl(src)) {
                child.remove();
                blockedCount++;
              }
            });
          }
        });
      });
      
      // Only log if we actually blocked something
      if (blockedCount > 0) {
        console.log(`🚫 [BLOCKED] Prevented ${blockedCount} external resources`);
      }
    } catch (error) {
      // Silently handle mutation processing errors
    }
  }

  private interceptNetworkRequests(): void {
    let blockedRequestCount = 0;
    
    // Intercept fetch
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input.toString();
      
      if (this.shouldBlockUrl(url)) {
        blockedRequestCount++;
        // Batch log blocked requests to reduce console noise
        if (blockedRequestCount % 5 === 0) {
          console.log(`🚫 [BLOCKED] ${blockedRequestCount} network requests intercepted`);
        }
        return new Response('{"blocked": true}', { 
          status: 200, 
          statusText: 'Blocked by development mode'
        });
      }
      
      return originalFetch(input, init);
    };

    // Intercept XMLHttpRequest
    const originalXMLHttpRequest = window.XMLHttpRequest;
    window.XMLHttpRequest = class extends originalXMLHttpRequest {
      open(method: string, url: string | URL, async?: boolean, user?: string | null, password?: string | null) {
        const urlString = url.toString();
        if (this.shouldBlockUrl(urlString)) {
          blockedRequestCount++;
          if (blockedRequestCount % 5 === 0) {
            console.log(`🚫 [BLOCKED] ${blockedRequestCount} XHR requests intercepted`);
          }
          // Create a mock response
          super.open(method, 'data:application/json,{"blocked":true}', async, user, password);
          return;
        }
        super.open(method, url, async, user, password);
      }
      
      private shouldBlockUrl = (url: string): boolean => {
        return globalScriptBlocker.shouldBlockUrl(url);
      };
    };

    console.log('🌐 [NETWORK] Request interception active - blocking external APIs');
  }

  private blockGlobalTrackingObjects(): void {
    let trackingCallCount = 0;
    
    // Block gtag with reduced logging
    if (!window.gtag) {
      window.gtag = (...args: any[]) => {
        trackingCallCount++;
        if (trackingCallCount % 10 === 1) {
          console.log(`🚫 [BLOCKED] ${trackingCallCount} tracking calls intercepted`);
        }
      };
    }

    // Block fbq (Facebook Pixel)
    if (!window.fbq) {
      window.fbq = (...args: any[]) => {
        trackingCallCount++;
        if (trackingCallCount % 10 === 1) {
          console.log(`🚫 [BLOCKED] ${trackingCallCount} tracking calls intercepted`);
        }
      };
    }

    // Block other common tracking objects
    const trackingObjects = ['ga', 'analytics', '_gaq', 'dataLayer'];
    trackingObjects.forEach(obj => {
      if (!window[obj]) {
        window[obj] = () => {
          trackingCallCount++;
          if (trackingCallCount % 10 === 1) {
            console.log(`🚫 [BLOCKED] ${trackingCallCount} tracking calls intercepted`);
          }
        };
      }
    });

    console.log('🛡️ [SHIELD] Global tracking objects blocked');
  }

  shouldBlockUrl(url: string): boolean {
    if (!this.config.isDevelopment) return false;
    
    // Never block Supabase URLs - they're essential for authentication
    if (url.includes('supabase.co') || url.includes('supabase.com')) {
      return false;
    }
    
    // Never block localhost or development URLs
    if (url.includes('localhost') || url.includes('127.0.0.1') || url.includes('lovableproject.com')) {
      return false;
    }
    
    // Never block data URLs or blob URLs
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return false;
    }
    
    return this.blockedDomains.some(domain => 
      url.includes(domain) || 
      url.startsWith(`https://${domain}`) || 
      url.startsWith(`http://${domain}`) ||
      url.startsWith(`//${domain}`)
    );
  }

  public destroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  public addBlockedDomain(domain: string): void {
    if (!this.blockedDomains.includes(domain)) {
      this.blockedDomains.push(domain);
      console.log(`➕ [ADDED] New blocked domain: ${domain}`);
    }
  }

  public getBlockedDomains(): string[] {
    return [...this.blockedDomains];
  }
}

export const globalScriptBlocker = new GlobalScriptBlocker();

// Add to window for debugging
if (typeof window !== 'undefined') {
  (window as any).globalScriptBlocker = globalScriptBlocker;
}

// Declare global types
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    ga: (...args: any[]) => void;
    analytics: (...args: any[]) => void;
    _gaq: any[];
    dataLayer: any[];
    [key: string]: any;
  }
}